import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Form, Spinner } from "react-bootstrap";
import { ArrowClockwise } from "react-bootstrap-icons";
import { NylasScheduling } from "@nylas/react";
import { fetchPreviewConfig } from "./slice";
import "./clientBookingSettings.css";

const ClientViewPreview = ({ profile, userProfile, isEditMode, editFormData }) => {
  const dispatch = useDispatch();
  const { previewData, previewStatus } = useSelector(
    (state) => state.clientBookingSettings
  );

  const durationOptions = useMemo(() => {
    const source = isEditMode && editFormData
      ? editFormData.durationRules
      : profile?.duration_rules;
    const rules = source || [];
    const sorted = [...rules].sort(
      (a, b) => (a.min_sqft || 0) - (b.min_sqft || 0)
    );
    const unique = [...new Set(sorted.map((r) => r.duration_minutes))].sort(
      (a, b) => a - b
    );
    return unique;
  }, [profile?.duration_rules, isEditMode, editFormData]);

  const [selectedDuration, setSelectedDuration] = useState(() => {
    return durationOptions[0] || 60;
  });

  // If selected duration is no longer in options, pick first
  useEffect(() => {
    if (durationOptions.length > 0 && !durationOptions.includes(selectedDuration)) {
      setSelectedDuration(durationOptions[0]);
    }
  }, [durationOptions, selectedDuration]);

  const getPreviewParams = useCallback(() => {
    if (isEditMode && editFormData) {
      return {
        durationMinutes: selectedDuration,
        minBufferTimeMinutes: editFormData.bufferTime,
        maxDaysInFuture: editFormData.bookingWindow,
        weeklyHours: editFormData.weeklyHours,
      };
    }
    if (!profile) return null;
    return {
      durationMinutes: selectedDuration,
      minBufferTimeMinutes: profile.min_buffer_time_minutes,
      maxDaysInFuture: profile.max_booking_horizon_days,
      weeklyHours: profile.weekly_hours,
    };
  }, [isEditMode, editFormData, profile, selectedDuration]);

  const handleRefresh = useCallback(() => {
    const params = getPreviewParams();
    if (!params) return;
    dispatch(fetchPreviewConfig(params));
  }, [dispatch, getPreviewParams]);

  // Auto-fetch preview on mount when profile is available
  const hasFetched = useRef(false);
  useEffect(() => {
    if (profile && !hasFetched.current) {
      hasFetched.current = true;
      handleRefresh();
    }
  }, [profile, handleRefresh]);

  // Re-fetch when duration changes (read-only mode only)
  const prevDuration = useRef(selectedDuration);
  useEffect(() => {
    if (
      profile &&
      hasFetched.current &&
      prevDuration.current !== selectedDuration &&
      !isEditMode
    ) {
      prevDuration.current = selectedDuration;
      handleRefresh();
    }
  }, [selectedDuration, profile, handleRefresh, isEditMode]);

  return (
    <div className="client-view-preview">
      {!isEditMode && <div className="client-view-overlay" />}

      <div className="client-view-header">
        <span className="client-view-title">Client View</span>
      </div>
      <div className="client-view-controls">
        <span className="client-view-duration-label">Duration</span>
        <Form.Select
          size="sm"
          className="client-view-duration-select"
          value={selectedDuration}
          onChange={(e) => setSelectedDuration(Number(e.target.value))}
        >
          {durationOptions.map((d) => (
            <option key={d} value={d}>
              {d} min
            </option>
          ))}
        </Form.Select>
        {isEditMode && (
          <Button
            variant="outline-primary"
            size="sm"
            className="cbs-preview-refresh-btn"
            onClick={handleRefresh}
            disabled={previewStatus === "loading"}
          >
            <ArrowClockwise size={14} className="me-1" />
            Refresh
          </Button>
        )}
      </div>

      <div className="client-view-scheduler">
        {previewStatus === "loading" ? (
          <div className="client-view-scheduler-placeholder">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading preview...
          </div>
        ) : previewData ? (
          <NylasScheduling
            configurationId={previewData.configuration_id}
            schedulerApiUrl={previewData.scheduler_api_url}
            nylasBranding={false}
          />
        ) : (
          <div className="client-view-scheduler-placeholder">
            Preview will appear here
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientViewPreview;
